import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewDialog({ trigger, onSubmitted }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ name: "", origin: "", quote: "" });

  const reset = () => {
    setForm({ name: "", origin: "", quote: "" });
    setRating(0);
    setHover(0);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error(t.reviews.errorRating);
      return;
    }
    if (form.quote.trim().length < 10) {
      toast.error(t.reviews.errorQuote);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        ...form,
        rating,
        language: lang,
      });
      toast.success(t.reviews.success);
      reset();
      setOpen(false);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      toast.error(t.reviews.error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#E5E0D8] focus:border-[#C85A3C] outline-none py-3 text-[#2C2520] placeholder-[#5C564F]/60 transition-colors caret-[#C85A3C]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#FAF9F6] border border-[#E5E0D8] max-w-lg p-0 overflow-hidden" data-testid="review-dialog">
        <div className="p-8 sm:p-10">
          <DialogHeader className="text-left">
            <p className="overline">{t.reviews.overline}</p>
            <DialogTitle className="font-serif-display text-3xl mt-2 text-[#2C2520]">
              {t.reviews.formTitle}
            </DialogTitle>
            <DialogDescription className="text-[#5C564F] mt-2 text-sm leading-relaxed">
              {t.reviews.formSub}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="review-form">
            <div>
              <label className="overline block mb-2">{t.reviews.rating}</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    data-testid={`review-star-${n}`}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${n} stelle`}
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        (hover || rating) >= n
                          ? "fill-[#C85A3C] text-[#C85A3C]"
                          : "text-[#E5E0D8]"
                      }`}
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">{t.reviews.name}</label>
                <input
                  data-testid="review-name"
                  required
                  maxLength={80}
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t.reviews.namePh}
                />
              </div>
              <div>
                <label className="overline block mb-2">{t.reviews.origin}</label>
                <input
                  data-testid="review-origin"
                  required
                  maxLength={80}
                  className={inputCls}
                  value={form.origin}
                  onChange={(e) => setForm({ ...form, origin: e.target.value })}
                  placeholder={t.reviews.originPh}
                />
              </div>
            </div>

            <div>
              <label className="overline block mb-2">{t.reviews.text}</label>
              <textarea
                data-testid="review-quote"
                required
                rows={4}
                minLength={10}
                maxLength={600}
                className={inputCls + " resize-none"}
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                placeholder={t.reviews.textPh}
              />
              <p className="text-xs text-[#5C564F]/60 mt-1 text-right">{form.quote.length}/600</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                data-testid="review-submit"
                disabled={submitting}
                className="btn-terracotta px-8 py-3.5 rounded-sm tracking-[0.2em] uppercase text-xs disabled:opacity-50 flex-1"
              >
                {submitting ? t.reviews.sending : t.reviews.submit}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-6 py-3.5 text-xs tracking-widest uppercase text-[#5C564F] hover:text-[#C85A3C] transition"
              >
                {t.reviews.cancel}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
